import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChevronRight } from "lucide-react";
import { fetchAdminBundle, removeBundleGallery, saveBundle, uploadBundleCover, uploadBundleGallery } from "../api/MerchandisingAPIs";
import { resolveMediaUrl } from "./Catalog/catalogUi";
import { useAuth } from "../utils/auth";
import { isAdmin } from "../utils/roles";
import { DimensionEditor, SinglePickerButton, emptySelection, selectionFromSlot } from "./BundleSelectionPicker";
import { fetchBrands } from "../api/BrandAPIs";
import { fetchCategories } from "../api/CategoryAPIs";

const emptySlot = () => ({
  name: "",
  description: "",
  isRequired: true,
  isGift: false,
  isFixed: false,
  countMode: "Units",
  minSelections: 1,
  maxSelections: 1,
  allowDuplicates: false,
  acceptsPersonalization: true,
  allowedDesignIds: "",
  allowedProductIds: "",
  allowedCategoryIds: "",
  allowedTypeIds: "",
  allowedCaseStyleIds: "",
  maxItemPrice: "",
  fixedDesignId: "",
  fixedProductId: "",
  fixedVariantId: "",
  fixedTypeId: "",
  fixedCaseStyleId: "",
  fixedQuantity: 1,
  fixedKind: "design",
  selection: emptySelection(),
});

const emptyReward = () => ({
  rewardType: "FreeShipping",
  slotIndex: "",
  isActive: true,
  minQualifyingUnits: "",
  minEligibleSubtotal: "",
  stackWithBundleDiscount: true,
  stackWithCoupon: false,
  stackWithOtherRewards: true,
  eligibleGovernorates: "",
  maxGiftPrice: "",
});

const ids = (value) => String(value || "").split(",").map((part) => Number(part.trim())).filter((id) => id > 0);
const asIdList = (value) => Array.isArray(value) ? value.map(Number).filter((id) => id > 0) : ids(value);
const publicDimension = (dimension) => {
  if (!dimension || !dimension.mode || dimension.mode === "None") return null;
  return {
    mode: dimension.mode,
    includeIds: dimension.includeIds || [],
    excludeIds: dimension.excludeIds || [],
    categoryIds: dimension.categoryIds || [],
    brandIds: dimension.brandIds || [],
  };
};
const num = (value) => value === "" || value == null ? null : Number(value);

function Hint({ text }) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState({ top: 0, left: 0, width: 280 });
  const anchor = useRef(null);
  const tip = useRef(null);
  const timer = useRef(null);
  const show = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const hide = () => {
    timer.current = setTimeout(() => setOpen(false), 180);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  useLayoutEffect(() => {
    if (!open || !anchor.current || !tip.current) return;
    const place = () => {
      const anchorBox = anchor.current.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - 16);
      const height = tip.current.getBoundingClientRect().height;
      let left = anchorBox.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      let top = anchorBox.bottom + 8;
      if (top + height > window.innerHeight - 8) top = Math.max(8, anchorBox.top - height - 8);
      setBox({ top, left, width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, text]);

  return (
    <span ref={anchor} className="relative inline-flex shrink-0" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        aria-label="شرح"
        aria-expanded={open}
        onFocus={show}
        onBlur={hide}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-purple-200 bg-purple-50 font-serif text-sm font-bold italic leading-none text-purple-700 hover:bg-purple-100"
      >
        i
      </button>
      {open && (
        <span
          ref={tip}
          role="tooltip"
          onMouseEnter={show}
          onMouseLeave={hide}
          style={{ position: "fixed", top: box.top, left: box.left, width: box.width }}
          className="z-50 rounded-xl bg-slate-900 px-3 py-2 text-right text-sm font-normal not-italic leading-6 text-white shadow-xl"
        >
          {text}
        </span>
      )}
    </span>
  );
}

function Hinted({ hint, children }) {
  return (
    <div className="flex items-start gap-2">
      <Hint text={hint} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function SectionTitle({ title, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <h2 className="font-bold">{title}</h2>
        <Hint text={hint} />
      </div>
      {children}
    </div>
  );
}

const BundleEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canEdit = isAdmin(authState.role);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    shortDescription: "",
    description: "",
    coverImageUrl: "",
    isActive: false,
    isFeatured: false,
    displayOrder: 0,
    pricingModel: "Percentage",
    fixedPrice: "",
    discountPercent: 10,
    discountAmount: "",
    maxDiscountAmount: "",
    minEligibleSubtotal: "",
    allowCoupon: false,
    slots: [emptySlot()],
    rewards: [],
  });
  const [recordId, setRecordId] = useState(id || null);
  const [errors, setErrors] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [catalogFilters, setCatalogFilters] = useState({ categories: [], brands: [] });

  useEffect(() => {
    if (!canEdit) return;
    Promise.all([fetchCategories().catch(() => []), fetchBrands().catch(() => [])]).then(([categories, brands]) => {
      const categoryRows = Array.isArray(categories) ? categories : [];
      const brandRows = Array.isArray(brands) ? brands : [];
      setCatalogFilters({
        categories: categoryRows.map((item) => ({ id: item.categoryId || item.id, name: item.name })),
        brands: brandRows.map((item) => ({ id: item.brandId || item.id, name: item.name })),
      });
    });
  }, [canEdit]);

  useEffect(() => {
    if (!canEdit) {
      navigate("/dashboard");
      return;
    }
    if (!id) return;
    fetchAdminBundle(id).then((bundle) => {
      setForm({
        ...bundle,
        slots: (bundle.slots || []).map((slot) => ({
          ...emptySlot(),
          ...slot,
          selection: selectionFromSlot(slot),
          fixedKind: slot.fixedProductId || slot.fixedVariantId ? "product" : "design",
        })),
        rewards: (bundle.rewards || []).map((reward) => ({
          ...emptyReward(),
          ...reward,
          slotIndex: (() => {
              const index = (bundle.slots || []).findIndex((slot) => slot.bundleSlotId === reward.bundleSlotId);
              return index >= 0 ? index : "";
            })(),
          eligibleGovernorates: (reward.eligibleGovernorates || []).join(","),
        })),
      });
    }).catch((error) => toast.error(error.message));
  }, [id, canEdit, navigate]);

  const updateSlot = (index, patch) => {
    setForm((prev) => ({ ...prev, slots: prev.slots.map((slot, slotIndex) => slotIndex === index ? { ...slot, ...patch } : slot) }));
  };

  const setDimension = (index, key, dimension) => {
    setForm((prev) => ({
      ...prev,
      slots: prev.slots.map((slot, slotIndex) => slotIndex !== index ? slot : {
        ...slot,
        selection: { ...(slot.selection || emptySelection()), [key]: dimension, touched: true },
      }),
    }));
  };

  const updateReward = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      rewards: prev.rewards.map((reward, rewardIndex) => rewardIndex === index ? { ...reward, ...patch } : reward),
    }));
  };

  const onSave = async (event) => {
    event.preventDefault();
    setErrors([]);
    try {
      const saved = await saveBundle(recordId, {
        ...form,
        gallery: (form.gallery || []).filter((item) => typeof item === "string" && !item.startsWith("blob:")),
        displayOrder: Number(form.displayOrder) || 0,
        fixedPrice: num(form.fixedPrice),
        discountPercent: num(form.discountPercent),
        discountAmount: num(form.discountAmount),
        maxDiscountAmount: num(form.maxDiscountAmount),
        minEligibleSubtotal: num(form.minEligibleSubtotal),
        slots: form.slots.map((slot, index) => ({
          ...slot,
          displayOrder: index,
          minSelections: Number(slot.minSelections) || 0,
          maxSelections: Number(slot.maxSelections) || 0,
          fixedQuantity: Number(slot.fixedQuantity) || 1,
          maxItemPrice: num(slot.maxItemPrice),
          fixedDesignId: num(slot.fixedDesignId),
          fixedProductId: num(slot.fixedProductId),
          fixedVariantId: num(slot.fixedVariantId),
          fixedTypeId: num(slot.fixedTypeId),
          fixedCaseStyleId: num(slot.fixedCaseStyleId),
          selection: slot.selection?.touched === false ? null : {
            designs: publicDimension(slot.selection?.designs),
            products: publicDimension(slot.selection?.products),
            variants: publicDimension(slot.selection?.variants),
            phoneModels: publicDimension(slot.selection?.phoneModels),
            caseStyles: publicDimension(slot.selection?.caseStyles),
          },
          allowedDesignIds: slot.selection?.touched === false ? asIdList(slot.allowedDesignIds) : [],
          allowedProductIds: slot.selection?.touched === false ? asIdList(slot.allowedProductIds) : [],
          allowedVariantIds: slot.selection?.touched === false ? asIdList(slot.allowedVariantIds) : [],
          allowedCategoryIds: slot.selection?.touched === false ? asIdList(slot.allowedCategoryIds) : [],
          allowedTypeIds: slot.selection?.touched === false ? asIdList(slot.allowedTypeIds) : [],
          allowedCaseStyleIds: slot.selection?.touched === false ? asIdList(slot.allowedCaseStyleIds) : [],
        })),
        rewards: form.rewards.map((reward, index) => ({
          ...reward,
          sortOrder: index,
          slotIndex: reward.slotIndex === "" ? null : Number(reward.slotIndex),
          minQualifyingUnits: num(reward.minQualifyingUnits),
          minEligibleSubtotal: num(reward.minEligibleSubtotal),
          maxGiftPrice: num(reward.maxGiftPrice),
          eligibleGovernorates: String(reward.eligibleGovernorates || "").split(",").map((part) => part.trim()).filter(Boolean),
        })),
      });
      const ownerId = saved.bundleId || recordId;
      setRecordId(ownerId);
      if (coverFile) await uploadBundleCover(ownerId, coverFile);
      if (galleryFiles.length) await uploadBundleGallery(ownerId, galleryFiles);
      toast.success("تم حفظ الباقة");
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverFile(null);
      setCoverPreview("");
      setGalleryFiles([]);
      if (!id) navigate(`/bundles/${saved.bundleId}`, { replace: true });
    } catch (error) {
      setErrors(error.message.split("\n"));
      toast.error("راجع أخطاء الإعداد");
    }
  };

  if (!canEdit) return null;

  return (
    <form onSubmit={onSave} className="min-h-screen space-y-4 bg-slate-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" rtl />
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-slate-900">{id ? "تعديل الباقة" : "باقة جديدة"}</h1>
        <button
          type="button"
          onClick={() => navigate("/bundles")}
          className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-8 py-4 text-xl font-bold text-slate-800 shadow-md hover:bg-slate-100"
        >
          <ChevronRight size={28} />
          رجوع
        </button>
      </div>
      {errors.length > 0 && (
        <div className="rounded-xl bg-red-50 p-3 text-red-700">
          {errors.map((error) => <p key={error}>{error}</p>)}
        </div>
      )}
      <section className="space-y-3 rounded-2xl bg-white p-4">
        <SectionTitle
          title="البيانات الأساسية"
          hint="دي بيانات صفحة الباقة نفسها: الاسم والرابط والصور، وهل الباقة ظاهرة للعملاء ولا لسه مسودة."
        />
        <Hinted hint="الاسم اللي يشوفه العميل في قائمة الباقات وفي صفحة الباقة.">
          <input className="w-full rounded-xl border px-3 py-2" placeholder="الاسم" value={form.name || ""} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </Hinted>
        <Hinted hint="جزء الرابط بعد /bundles/. مثال: summer-case. لو سيبته فاضي يتولد من الاسم، ولازم يكون غير مكرر.">
          <input className="w-full rounded-xl border px-3 py-2" placeholder="الرابط" value={form.slug || ""} onChange={(event) => setForm({ ...form, slug: event.target.value })} />
        </Hinted>
        <Hinted hint="سطر مختصر يظهر تحت الاسم في بطاقة الباقة.">
          <textarea className="w-full rounded-xl border px-3 py-2" placeholder="وصف قصير" value={form.shortDescription || ""} onChange={(event) => setForm({ ...form, shortDescription: event.target.value })} />
        </Hinted>
        <Hinted hint="الشرح الكامل داخل صفحة الباقة.">
          <textarea className="w-full rounded-xl border px-3 py-2" placeholder="الوصف" value={form.description || ""} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Hinted>
        <Hinted hint="الصورة الرئيسية للباقة. بتتحفظ على السيرفر في فولدر الباقة.">
          <div>
            <p className="mb-2 font-semibold">صورة الغلاف</p>
            {(coverPreview || form.coverImageUrl) && (
              <img src={coverPreview || resolveMediaUrl(form.coverImageUrl)} alt="" className="mb-3 h-44 w-full max-w-md rounded-xl object-cover" />
            )}
            <input className="w-full rounded-xl border px-3 py-2" type="file" accept="image/*" onChange={(event) => {
              const file = event.target.files?.[0];
              if (coverPreview) URL.revokeObjectURL(coverPreview);
              setCoverFile(file || null);
              setCoverPreview(file ? URL.createObjectURL(file) : "");
            }} />
          </div>
        </Hinted>
        <Hinted hint="صور زيادة تظهر في صفحة الباقة مع الغلاف. حد أقصى 8 صور.">
          <div>
            <p className="mb-2 font-semibold">صور إضافية</p>
            <div className="mb-3 flex flex-wrap gap-3">
              {(form.gallery || []).map((path) => (
                <div key={path}>
                  <img src={resolveMediaUrl(path)} alt="" className="h-24 w-24 rounded-xl object-cover" />
                  <button type="button" className="mt-1 text-sm text-red-600" onClick={async () => {
                    if (!id) return;
                    try {
                      const result = await removeBundleGallery(id, path);
                      setForm((prev) => ({ ...prev, gallery: result.gallery || [] }));
                    } catch (error) {
                      toast.error(error.message);
                    }
                  }}>حذف</button>
                </div>
              ))}
              {galleryFiles.map((file) => <span key={file.name} className="text-sm text-slate-600">{file.name}</span>)}
            </div>
            <input className="w-full rounded-xl border px-3 py-2" type="file" accept="image/*" multiple onChange={(event) => setGalleryFiles(Array.from(event.target.files || []))} />
          </div>
        </Hinted>
        <Hinted hint="لو متعلم، الباقة تظهر للعملاء بعد الحفظ بشرط إن الإعداد يكون سليم. لو مش متعلم تفضل مسودة ومحدش يشوفها.">
          <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(form.isActive)} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} /> نشر بعد التحقق</label>
        </Hinted>
        <Hinted hint="علامة تحفظ على الباقة لو حابب تميّزها عن باقي الباقات.">
          <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(form.isFeatured)} onChange={(event) => setForm({ ...form, isFeatured: event.target.checked })} /> مميزة</label>
        </Hinted>
      </section>
      <section className="space-y-3 rounded-2xl bg-white p-4">
        <SectionTitle
          title="التسعير"
          hint="طريقة حساب سعر الباقة على القطع اللي العميل يختارها. الخصم يتطبق على مكونات الباقة فقط، والقطع دي مش بتدخل عرض اشتري 2 وخد 1."
        />
        <Hinted hint="سعر المكونات: العميل يدفع سعر كل قطعة من غير خصم باقة. سعر ثابت: الباقة كلها بسعر واحد. نسبة خصم: تخصم نسبة من مجموع القطع. خصم مبلغ ثابت: يخصم مبلغ محدد من المجموع.">
          <select className="w-full rounded-xl border px-3 py-2" value={form.pricingModel} onChange={(event) => setForm({ ...form, pricingModel: event.target.value })}>
            <option value="None">سعر المكونات</option>
            <option value="FixedPrice">سعر ثابت للباقة</option>
            <option value="Percentage">نسبة خصم</option>
            <option value="FixedAmount">خصم مبلغ ثابت</option>
          </select>
        </Hinted>
        <Hinted hint="استخدمه مع «سعر ثابت للباقة». ده سعر الباقة الواحدة بالجنيه، ولو العميل اشترى الباقة مرتين السعر يتضرب في 2.">
          <input className="w-full rounded-xl border px-3 py-2" placeholder="السعر الثابت" value={form.fixedPrice ?? ""} onChange={(event) => setForm({ ...form, fixedPrice: event.target.value })} />
        </Hinted>
        <Hinted hint="استخدمه مع «نسبة خصم». رقم من 1 إلى 100، يخصم من مجموع أسعار المكونات. مثال: 10 يعني خصم 10%.">
          <input className="w-full rounded-xl border px-3 py-2" placeholder="نسبة الخصم" value={form.discountPercent ?? ""} onChange={(event) => setForm({ ...form, discountPercent: event.target.value })} />
        </Hinted>
        <Hinted hint="استخدمه مع «خصم مبلغ ثابت». مبلغ بالجنيه يخصم من مجموع المكونات لكل باقة واحدة.">
          <input className="w-full rounded-xl border px-3 py-2" placeholder="مبلغ الخصم" value={form.discountAmount ?? ""} onChange={(event) => setForm({ ...form, discountAmount: event.target.value })} />
        </Hinted>
        <Hinted hint="سقف للخصم لما التسعير يكون نسبة. مثال: 20% بس مش أكتر من 50 جنيه للباقة. سيبه فاضي لو مفيش سقف.">
          <input className="w-full rounded-xl border px-3 py-2" placeholder="أقصى خصم بالنسبة" value={form.maxDiscountAmount ?? ""} onChange={(event) => setForm({ ...form, maxDiscountAmount: event.target.value })} />
        </Hinted>
        <Hinted hint="افتراضياً كوبون الخصم ما يتطبقش على قطع الباقة. لو علّمت هنا، الكوبون يقدر يخصم منها كمان. باقي السلة بره الباقة يتأثر بالكوبون في الحالتين.">
          <label className="flex items-center gap-2"><input type="checkbox" checked={Boolean(form.allowCoupon)} onChange={(event) => setForm({ ...form, allowCoupon: event.target.checked })} /> السماح بكوبون على مكونات الباقة</label>
        </Hinted>
        <p className="text-xs text-slate-500">مكونات الباقة لا تدخل عرض اشتري 2 وخد 1. الكوبون يُحسب على باقي السلة إلا إذا سمحت به هنا.</p>
      </section>
      <section className="space-y-3 rounded-2xl bg-white p-4">
        <SectionTitle
          title="الخانات"
          hint="الخانة خطوة اختيار جوه الباقة. كل خانة بتحدد العميل يختار إيه: جراب، هدية، أو منتج ثابت. الباقة لازم يكون فيها خانة واحدة على الأقل."
        >
          <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={() => setForm({ ...form, slots: [...form.slots, emptySlot()] })}>إضافة خانة</button>
        </SectionTitle>
        {form.slots.map((slot, index) => (
          <div key={index} className="space-y-2 rounded-xl border p-3">
            <Hinted hint="العنوان اللي يشوفه العميل، مثل «اختَر الجراب» أو «الهدية».">
              <input className="w-full rounded-lg border px-2 py-1" placeholder="اسم الخانة" value={slot.name} onChange={(event) => updateSlot(index, { name: event.target.value })} />
            </Hinted>
            <Hinted hint="شرح قصير يظهر للعميل تحت اسم الخانة عشان يعرف يختار إيه.">
              <textarea className="w-full rounded-lg border px-2 py-1" placeholder="تعليمات للعميل" value={slot.description || ""} onChange={(event) => updateSlot(index, { description: event.target.value })} />
            </Hinted>
            <Hinted hint="العميل لازم يكمّل الخانة دي قبل ما يضيف الباقة. لو مش متعلم يقدر يسيبها فاضية.">
              <label className="flex items-center gap-2"><input type="checkbox" checked={slot.isRequired} onChange={(event) => updateSlot(index, { isRequired: event.target.checked })} /> مطلوبة</label>
            </Hinted>
            <Hinted hint="قطع الخانة دي هدايا بسعر صفر، ومش بتتحسب في سعر الباقة. لازم تربطها بمكافأة من نوع هدية تحت.">
              <label className="flex items-center gap-2"><input type="checkbox" checked={slot.isGift} onChange={(event) => updateSlot(index, { isGift: event.target.checked })} /> هدية</label>
            </Hinted>
            <Hinted hint="منتج أنت بتحدده، والعميل مش بيغيره. لازم تحط رقم تصميم أو منتج أو متغير واحد بس.">
              <label className="flex items-center gap-2"><input type="checkbox" checked={slot.isFixed} onChange={(event) => updateSlot(index, { isFixed: event.target.checked })} /> مكون ثابت</label>
            </Hinted>
            <Hinted hint="يسمح يختار نفس المنتج أكتر من مرة في الخانة. لو مش متعلم، كل منتج يتختار مرة واحدة.">
              <label className="flex items-center gap-2"><input type="checkbox" checked={slot.allowDuplicates} onChange={(event) => updateSlot(index, { allowDuplicates: event.target.checked })} /> السماح بالتكرار</label>
            </Hinted>
            <Hinted hint="أقل عدد لازم العميل يختاره في الخانة. الخانة المطلوبة لازم يكون 1 على الأقل.">
              <input className="w-full rounded-lg border px-2 py-1" type="number" placeholder="الأدنى" value={slot.minSelections} onChange={(event) => updateSlot(index, { minSelections: event.target.value })} />
            </Hinted>
            <Hinted hint="أكبر عدد يقدر يختاره. لازم يكون أكبر من أو يساوي الأدنى.">
              <input className="w-full rounded-lg border px-2 py-1" type="number" placeholder="الأقصى" value={slot.maxSelections} onChange={(event) => updateSlot(index, { maxSelections: event.target.value })} />
            </Hinted>
            <Hinted hint="عدد القطع يعد الكمية: جرابين من نفس التصميم يعدّوا 2. عدد الاختيارات يعد المنتجات المختلفة، وكل اختيار كميته تساوي عدد الباقات.">
              <select className="w-full rounded-lg border px-2 py-1" value={slot.countMode} onChange={(event) => updateSlot(index, { countMode: event.target.value })}>
                <option value="Units">عدد القطع</option>
                <option value="Selections">عدد الاختيارات</option>
              </select>
            </Hinted>
            {!slot.isFixed && (
              <div className="space-y-3">
                <DimensionEditor title="التصاميم" noun="التصاميم" kind="design" ruleKind="category" dimension={slot.selection?.designs} filters={catalogFilters} onChange={(dimension) => setDimension(index, "designs", dimension)} />
                <DimensionEditor title="المنتجات الجاهزة" noun="المنتجات" kind="product" ruleKind="category" dimension={slot.selection?.products} filters={catalogFilters} onChange={(dimension) => setDimension(index, "products", dimension)} />
                <DimensionEditor title="المتغيرات" noun="المتغيرات" kind="variant" dimension={slot.selection?.variants} filters={{ productId: slot.fixedProductId || undefined }} onChange={(dimension) => setDimension(index, "variants", dimension)} />
                <DimensionEditor title="موديلات الهواتف" noun="الموديلات" kind="phone" ruleKind="brand" dimension={slot.selection?.phoneModels} filters={catalogFilters} onChange={(dimension) => setDimension(index, "phoneModels", dimension)} />
                <DimensionEditor title="أشكال الجراب" noun="الأشكال" kind="caseStyle" dimension={slot.selection?.caseStyles} onChange={(dimension) => setDimension(index, "caseStyles", dimension)} />
              </div>
            )}
            <Hinted hint="العميل ما يقدرش يختار قطعة سعرها أعلى من الرقم ده. سيبه فاضي لو مفيش حد.">
              <input className="w-full rounded-lg border px-2 py-1" placeholder="أقصى سعر للقطعة" value={slot.maxItemPrice ?? ""} onChange={(event) => updateSlot(index, { maxItemPrice: event.target.value })} />
            </Hinted>
            {slot.isFixed && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <button type="button" className={`rounded-full px-3 py-1 text-sm ${slot.fixedKind === "product" ? "bg-slate-100" : "bg-purple-700 text-white"}`} onClick={() => updateSlot(index, { fixedKind: "design", fixedProductId: "", fixedVariantId: "" })}>تصميم</button>
                  <button type="button" className={`rounded-full px-3 py-1 text-sm ${slot.fixedKind === "product" ? "bg-purple-700 text-white" : "bg-slate-100"}`} onClick={() => updateSlot(index, { fixedKind: "product", fixedDesignId: "", fixedTypeId: "", fixedCaseStyleId: "" })}>منتج جاهز</button>
                </div>
                {slot.fixedKind !== "product" ? (
                  <>
                    <SinglePickerButton label="التصميم" kind="design" selectedId={slot.fixedDesignId} filters={catalogFilters} onChange={(designId) => updateSlot(index, { fixedDesignId: designId || "", fixedTypeId: "", fixedCaseStyleId: "" })} />
                    <SinglePickerButton label="موديل الموبايل" kind="phone" selectedId={slot.fixedTypeId} filters={catalogFilters} onChange={(typeId) => updateSlot(index, { fixedTypeId: typeId || "", fixedCaseStyleId: "" })} />
                    <SinglePickerButton label="شكل الجراب" kind="caseStyle" selectedId={slot.fixedCaseStyleId} filters={{ typeId: slot.fixedTypeId || undefined }} onChange={(styleId) => updateSlot(index, { fixedCaseStyleId: styleId || "" })} />
                  </>
                ) : (
                  <>
                    <SinglePickerButton label="المنتج" kind="product" selectedId={slot.fixedProductId} filters={catalogFilters} onChange={(productId) => updateSlot(index, { fixedProductId: productId || "", fixedVariantId: "", fixedDesignId: "" })} />
                    <SinglePickerButton label="المتغير" kind="variant" selectedId={slot.fixedVariantId} filters={{ productId: slot.fixedProductId || undefined }} onChange={(variantId) => updateSlot(index, { fixedVariantId: variantId || "" })} />
                  </>
                )}
              </div>
            )}
            <button type="button" className="text-red-600" onClick={() => setForm({ ...form, slots: form.slots.filter((_, slotIndex) => slotIndex !== index) })}>حذف الخانة</button>
          </div>
        ))}
      </section>
      <section className="space-y-3 rounded-2xl bg-white p-4">
        <SectionTitle
          title="المكافآت"
          hint="المكافأة زيادة فوق خصم الباقة: يا شحن مجاني، يا هدية من خانة هدية. بتتطبق بس لو شروطها اتحققت."
        >
          <button type="button" className="rounded-lg bg-slate-100 px-3 py-1" onClick={() => setForm({ ...form, rewards: [...form.rewards, emptyReward()] })}>إضافة مكافأة</button>
        </SectionTitle>
        {form.rewards.map((reward, index) => (
          <div key={index} className="space-y-2 rounded-xl border p-3">
            <Hinted hint="شحن مجاني يلغي شحن العميل لو الشروط تمام. هدية لازم تختار لها خانة متعلم عليها «هدية»، والهدية سعرها صفر.">
              <select className="w-full rounded-lg border px-2 py-1" value={reward.rewardType} onChange={(event) => updateReward(index, { rewardType: event.target.value })}>
                <option value="FreeShipping">شحن مجاني</option>
                <option value="Gift">هدية</option>
              </select>
            </Hinted>
            <Hinted hint="للهدية: اختَر الخانة اللي العميل ياخد منها الهدية. للشحن المجاني تقدر تسيبها بدون خانة.">
              <select className="w-full rounded-lg border px-2 py-1" value={reward.slotIndex} onChange={(event) => updateReward(index, { slotIndex: event.target.value })}>
                <option value="">بدون خانة</option>
                {form.slots.map((slot, slotIndex) => <option key={slotIndex} value={slotIndex}>{slot.name || `خانة ${slotIndex + 1}`}</option>)}
              </select>
            </Hinted>
            <Hinted hint="أقل عدد قطع مدفوعة في الباقة، من غير الهدايا، عشان المكافأة تتفعل. سيبه فاضي لو مفيش شرط عدد.">
              <input className="w-full rounded-lg border px-2 py-1" placeholder="أقل عدد قطع مدفوعة" value={reward.minQualifyingUnits ?? ""} onChange={(event) => updateReward(index, { minQualifyingUnits: event.target.value })} />
            </Hinted>
            <Hinted hint="أسماء المحافظات اللي ينطبق عليها الشحن المجاني، مفصولة بفاصلة. سيبها فاضية لو الشحن المجاني لكل المحافظات.">
              <input className="w-full rounded-lg border px-2 py-1" placeholder="محافظات الشحن، مفصولة بفاصلة" value={reward.eligibleGovernorates || ""} onChange={(event) => updateReward(index, { eligibleGovernorates: event.target.value })} />
            </Hinted>
            <Hinted hint="لو متعلم، الشحن المجاني يفضل حتى لو العميل استخدم كوبون. لو مش متعلم، وجود كوبون يلغي الشحن المجاني.">
              <label className="flex items-center gap-2"><input type="checkbox" checked={reward.stackWithCoupon} onChange={(event) => updateReward(index, { stackWithCoupon: event.target.checked })} /> يتجمع مع الكوبون</label>
            </Hinted>
            <Hinted hint="لو متعلم، المكافأة تتطبق حتى لو الباقة نفسها عليها خصم. لو مش متعلم، المكافأة تتلغي لما يكون فيه خصم باقة.">
              <label className="flex items-center gap-2"><input type="checkbox" checked={reward.stackWithBundleDiscount} onChange={(event) => updateReward(index, { stackWithBundleDiscount: event.target.checked })} /> يتجمع مع خصم الباقة</label>
            </Hinted>
            <Hinted hint="لو متعلم، المكافأة دي تتطبق مع مكافآت تانية. لو مش متعلم، تبقى لوحدها ومينفعش تجمعها مع مكافأة تانية.">
              <label className="flex items-center gap-2"><input type="checkbox" checked={reward.stackWithOtherRewards} onChange={(event) => updateReward(index, { stackWithOtherRewards: event.target.checked })} /> يتجمع مع المكافآت الأخرى</label>
            </Hinted>
          </div>
        ))}
      </section>
      <button type="submit" className="w-full rounded-xl bg-purple-700 py-3 text-white">حفظ والتحقق</button>
    </form>
  );
};

export default BundleEditorPage;
